/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';

import { IFooterViewProps } from '@msdyn365-commerce-modules/footer/src/modules/footer-item/./footer-item';
import { HEADER_FOOTER, KWR } from '../../../utilities/constants';
import { getToggleLang, getUserPrefLang, getUserPreferredLanguage, setCustomerLangInCache } from '../../../utilities/language-change';
import { IFooterItemProps, IFooterItemConfig, ILinkInMaoriData } from '../definition-extensions/footer-item.ext.props.autogenerated';
import { onTelemetryClick, getTelemetryObject, getPayloadObject, getTelemetryAttributes } from '@msdyn365-commerce-modules/utilities';

/**
 *
 * FooterItemView component.
 * @extends {React.PureComponent<IFooterViewProps>}
 */
export class FooterItemView extends React.PureComponent<IFooterViewProps & IFooterItemProps<{}>> {
    public render(): JSX.Element | null {
        const { FooterItem } = this.props;
        return <Module {...FooterItem}>{this._renderElement(this.props as any)}</Module>;
    }

    private _createImageMarkup(
        item: Msdyn365.IImageData | undefined,
        preferredLanguage: string,
        imageTitleInMaori: any,
        ariaHiddenAttr?: boolean
    ): React.ReactNode | null {
        if (!item || !item.src || item.src.length === 0) {
            return null;
        }

        // const defaultImageSettings: Msdyn365.IImageSettings = {
        //     viewports: {
        //         xs: { q: 'w=24&h=24&m=6', w: 0, h: 0 },
        //         lg: { q: 'w=24&h=24&m=6', w: 0, h: 0 }
        //     },
        //     lazyload: true
        // };
        return (
            <Msdyn365.Image
                {...item}
                title={preferredLanguage === 'maori' ? imageTitleInMaori : item.title}
                requestContext={this.props.context.request}
                gridSettings={this.props.context.request.gridSettings!}
                imageSettings={item.imageSettings}
                loadFailureBehavior='default'
                aria-hidden={ariaHiddenAttr}
                editProps={{ key: this.props.config.image || {}, requestContext: this.props.context.request }}
                shouldSkipToMainImage
            />
        );
    }

    public handleLinkTextChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.link!.linkText = event.target.value);

    private _createEditableLink(linkInMaori: ILinkInMaoriData | undefined): React.ReactNode | null {
        const linkText = linkInMaori && linkInMaori.linkText ? linkInMaori.linkText : '';
        const telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        const payLoad = getPayloadObject('click', telemetryContent, '', '');
        payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(telemetryContent, payLoad);

        if (linkInMaori) {
            const linkText = linkInMaori.linkText ? linkInMaori.linkText : '';

            // TODO: Update the inner tag in the anchor link to span
            const editableLink: Msdyn365.ILinksData = {
                ariaLabel: linkInMaori.ariaLabel,
                className: 'ms-footer__link',
                linkText,
                linkUrl: linkInMaori.linkUrl.destinationUrl,
                openInNewTab: linkInMaori.openInNewTab,
                role: 'link',
                additionalProperties: attributes!,
                onClick: onTelemetryClick(telemetryContent, payLoad, linkText)
            };
            return (
                <Msdyn365.Link
                    link={editableLink}
                    editProps={{ onTextChange: this.handleLinkTextChange, requestContext: this.props.context.request }}
                />
            );
        }
        return null;
    }

    private _createLinkInMaori(linkInMaori: ILinkInMaoriData | undefined) {
        const linkText = linkInMaori && linkInMaori.linkText ? linkInMaori.linkText : '';
        const telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        const payLoad = getPayloadObject('click', telemetryContent, '', '');
        payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(telemetryContent, payLoad);

        return (
            linkInMaori &&
            linkInMaori.linkUrl &&
            linkInMaori.linkUrl.destinationUrl && {
                tag: 'a',
                role: 'link',
                className: 'ms-footer__link',
                href: linkInMaori.linkUrl.destinationUrl,
                title: linkText,
                'aria-label': linkInMaori.ariaLabel,
                target: linkInMaori.openInNewTab ? '_blank' : undefined,
                onClick: onTelemetryClick(telemetryContent, payLoad, linkText),
                ...attributes
            }
        );
    }

    private _renderElement(props: IFooterViewProps): React.ReactNode | null {
        const { heading, HeadingContainer, Link, linkText, text, EditableLink, context } = props;
        const { image, linkInMaori, imageTitleInMaori } = props.config as IFooterItemConfig;
        const LinkInMaori = this._createLinkInMaori(linkInMaori);
        const EditableLinkInMaori = this._createEditableLink(linkInMaori);

        // update user preferred language - For language preference, updation happens only in footer as it is common across the sites
        if (context.request.user.isAuthenticated) {
            setCustomerLangInCache({ context });
        }

        // Identifying Language
        const toggleLang = getToggleLang(context);
        const userPrefLang = getUserPrefLang(context.request.user.customerAccountNumber as any, context);
        const preferredLanguage: any = getUserPreferredLanguage(KWR, HEADER_FOOTER, userPrefLang, toggleLang, context);

        const NewEditableLink = preferredLanguage === 'maori' ? EditableLinkInMaori : EditableLink;
        const newImage = this._createImageMarkup(image, preferredLanguage, imageTitleInMaori);

        if (heading) {
            return <Node {...HeadingContainer}> {heading} </Node>;
        } else if ((Link || LinkInMaori) && newImage) {
            return (
                <Node {...Link}>
                    {preferredLanguage === 'maori' ? linkInMaori?.linkText : linkText}
                    {newImage}
                </Node>
            );
        } else if (Link || LinkInMaori) {
            return NewEditableLink;
        }
        return (
            <>
                {text}
                {newImage}
            </>
        );
    }
}

export default FooterItemView;
